import { Box, Typography } from "@mui/material";
import { QUIZ } from "@/theme/quizTokens";
import type { PlanDays } from "@/helpers/plansToPacks";
import { quizAsset } from "@/components/quiz/shared/quizAssets";

type Props = {
    selectedPlanDays: PlanDays;
    dogName?: string;
};

type Step = {
    number: number;
    circleColor: string;
    title: string;
    body: React.ReactNode;
    imageSrc: string; // real Shopify CDN url
    imageAlt: string;
};

// Freezer footprint copy — varies by selected plan cadence (Audit Page B, Item #15)
const FREEZER_FOOTPRINT: Record<PlanDays, string> = {
    7: "about one shelf",
    14: "about one drawer",
    28: "about two drawers",
};

export default function PlanDeliveryGuide({ selectedPlanDays, dogName }: Props) {
    const name = dogName?.trim() || "your dog";
    const footprint = FREEZER_FOOTPRINT[selectedPlanDays] ?? FREEZER_FOOTPRINT[14];
    const showTightOnSpaceNote = selectedPlanDays === 14 || selectedPlanDays === 28;

    const steps: Step[] = [
        {
            number: 1,
            circleColor: QUIZ.color.lavender,
            title: "It arrives frozen",
            body: "In insulated, recyclable packaging that keeps everything solid in transit.",
            imageSrc: quizAsset("delivery-frozen-box"),
            imageAlt: "Insulated box on doorstep",
        },
        {
            number: 2,
            circleColor: QUIZ.color.coral,
            title: "Not in? No problem",
            body: "It stays frozen for up to 48 hours, so the doorstep or a neighbour is fine.",
            imageSrc: quizAsset("delivery-dog-with-box"),
            imageAlt: "48 hour delivery badge",
        },
        {
            number: 3,
            circleColor: QUIZ.color.green,
            title: "Defrost a few tubs in the fridge",
            body: (
                <>
                    The rest go in the freezer. A {selectedPlanDays}-day box takes{" "}
                    {footprint}.
                    {showTightOnSpaceNote && (
                        <>
                            <br />
                            <br />
                            <Box component="span" sx={{ fontWeight: 700 }}>
                                Tight on freezer space?
                            </Box>{" "}
                            The 7-day plan needs about one shelf.
                        </>
                    )}
                </>
            ),
            imageSrc: quizAsset("delivery-fridge-tubs"),
            imageAlt: "Open fridge with tubs stacked",
        },
        {
            number: 4,
            circleColor: QUIZ.color.blue,
            title: `Serve, and watch the tail wags`,
            body: `No weighing, no measuring. ${name}'s portion is already done for you.`,
            // Plain full-bleed photo — same treatment as every other card.
            // Resolved through the shared quiz asset pipeline (theme-injected
            // window.__QUIZ_ASSETS__, falling back to the CDN/Figma URL below),
            // same as every other image on this quiz.
            imageSrc: quizAsset("hiw-dog-cutout"),
            imageAlt: `${name} enjoying their meal`,
        },
    ];

    return (
        <Box sx={{ width: "100%", bgcolor: QUIZ.color.cream, py: { xs: "32px", md: "44px" } }}>
            <Box
                sx={{
                    width: { xs: "100%", sm: 520, md: "100%" },
                    maxWidth: { xs: "100%", sm: 520, md: 1320 },
                    mx: "auto",
                    px: { xs: "28px", md: "24px" },
                }}
            >
                {/* "DELIVERY & STORAGE" label */}
                <Typography
                    sx={{
                        fontFamily: QUIZ.font.button,
                        fontWeight: 700,
                        fontSize: { xs: 13, md: 16 },
                        letterSpacing: "1px",
                        textAlign: "center",
                        color: QUIZ.color.brown,
                        mb: 1,
                    }}
                >
                    DELIVERY &amp; STORAGE
                </Typography>

                {/* Heading */}
                <Typography
                    component="h2"
                    sx={{
                        fontFamily: QUIZ.font.display,
                        fontWeight: 900,
                        textTransform: "uppercase",
                        textAlign: "center",
                        color: QUIZ.color.brown,
                        fontSize: { xs: 28, md: 42 },
                        lineHeight: 1.15,
                        mb: { xs: 3, md: 4 },
                    }}
                >
                    What happens when it arrives
                </Typography>

                {/* Step cards — single column on mobile, explicit 3-column grid on desktop:
                    col 1 = card 1 (full height), col 2 = cards 2+3 stacked, col 3 = card 4 (full height).
                    Every card — including card 4 — uses the exact same "text on top, plain
                    full-bleed photo below" layout. No special frame/cutout treatment anywhere. */}
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                        gridTemplateAreas: {
                            xs: `"c1" "c2" "c3" "c4"`,
                            md: `"c1 c2 c4" "c1 c3 c4"`,
                        },
                        gap: { xs: "20px", md: "28px" },
                    }}
                >
                    {steps.map((step) => (
                        <Box
                            key={step.number}
                            sx={{
                                gridArea: `c${step.number}`,
                                bgcolor: QUIZ.color.brown,
                                borderRadius: "20px",
                                overflow: "hidden",
                                display: "flex",
                                flexDirection: "column",
                                minWidth: 0, // prevents long unwrapped text from pushing the grid column wider than its track
                                // Fixed height on mobile so all cards line up evenly regardless of
                                // how many lines their title/body copy wraps to. Desktop grid rows
                                // already equalize themselves via gridTemplateAreas, so leave "auto" there.
                                minHeight: { xs: "480px", md: "auto" },
                            }}
                        >
                            {/* Text portion */}
                            <Box sx={{ p: { xs: "28px 20px 24px", md: "36px 40px 28px" }, textAlign: "center" }}>
                                {/* Step badge — 59px circle per Figma spec */}
                                <Box
                                    sx={{
                                        width: "59px",
                                        height: "59px",
                                        borderRadius: "50%",
                                        bgcolor: step.circleColor,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        mx: "auto",
                                        mb: { xs: "16px", md: "20px" },
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontFamily: QUIZ.font.display,
                                            fontWeight: 900,
                                            fontSize: "26px",
                                            color: QUIZ.color.brown,
                                        }}
                                    >
                                        {step.number}
                                    </Typography>
                                </Box>

                                <Typography
                                    component="h3"
                                    sx={{
                                        fontFamily: QUIZ.font.display,
                                        fontWeight: 900,
                                        textTransform: "uppercase",
                                        color: QUIZ.color.cream,
                                        fontSize: { xs: 22, md: 28 },
                                        lineHeight: 1.2,
                                        mb: { xs: "12px", md: "16px" },
                                    }}
                                >
                                    {step.title}
                                </Typography>

                                <Typography
                                    sx={{
                                        fontFamily: QUIZ.font.body,
                                        fontSize: { xs: 14, md: 16 },
                                        lineHeight: 1.5,
                                        color: QUIZ.color.cream,
                                        opacity: 0.9,
                                        maxWidth: 420,
                                        mx: "auto",
                                    }}
                                >
                                    {step.body}
                                </Typography>
                            </Box>

                            {/* Image band — plain full-bleed photo, same for every card
                                (including card 4). No frame, no cutout, no placeholder. */}
                            {step.number === 4 ? (
                                <Box sx={{ position: "relative", flexGrow: 1, minHeight: { xs: "220px", md: "300px" } }}>
                                    <Box
                                        sx={{
                                            position: "absolute", left: 0, right: 0, bottom: 0,
                                            top: { xs: "14%", md: "13%" }, bgcolor: QUIZ.color.blue,
                                            borderRadius: "10px 10px 0 0",
                                        }}
                                    />
                                    <Box
                                        component="img"
                                        src={step.imageSrc}
                                        alt={step.imageAlt}
                                        sx={{
                                            position: "absolute", zIndex: 1, bottom: 0, left: "50%",
                                            transform: "translateX(-50%)", height: "100%", width: "auto",
                                            maxWidth: "none", display: "block",
                                        }}
                                    />
                                </Box>
                            ) : (
                                <Box sx={{ flexGrow: 1, minHeight: { xs: "140px", md: "180px" }, bgcolor: QUIZ.color.cream3 }}>
                                    <Box
                                        component="img"
                                        src={step.imageSrc}
                                        alt={step.imageAlt}
                                        sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                                    />
                                </Box>
                            )}
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
}
